import {
  allFactoriesBaselinesControllerArchiveLine,
  allFactoriesBaselinesControllerCreateLine,
  allFactoriesBaselinesControllerGetBaselines,
  allFactoriesBaselinesControllerListAvailableCostLines,
  allFactoriesBaselinesControllerPutChildBaseline,
  allFactoriesBaselinesControllerPutChildPeriodValue,
  allFactoriesBaselinesControllerPutParentBaseline,
  allFactoriesBaselinesControllerPutParentPeriodValue,
  allFactoriesBaselinesControllerUnarchiveLine,
  allFactoriesBaselinesControllerUpdateLine,
  ApiError,
  auditControllerList,
  operatingCostsControllerArchiveCostLine,
  operatingCostsControllerCreateCostLine,
  operatingCostsControllerGetBaselines,
  operatingCostsControllerListAvailableCostLines,
  operatingCostsControllerListCostLines,
  operatingCostsControllerPutBaseline,
  operatingCostsControllerPutPeriodValue,
  operatingCostsControllerUnarchiveCostLine,
  operatingCostsControllerUpdateCostLine,
  partnerCostCategoriesControllerArchive,
  partnerCostCategoriesControllerCreate,
  projectsControllerListProjects,
  supportLogControllerGetDay,
  supportLogControllerGetSummary,
  supportLogControllerPutEntry,
  workersControllerListWorkers,
  type AllFactoriesBaselinesResponseDto,
  type AuditPageDto,
  type AvailableCostLinesResponseDto,
  type BaselinesResponseDto,
  type BaselineVersionDto,
  type CostCategoryDto,
  type CostLineDto,
  type CostLinesResponseDto,
  type CreateCostLineDto,
  type CreateCustomCostCategoryDto,
  type PeriodValueResponseDto,
  type ProjectListItemDto,
  type PutBaselineDto,
  type PutPeriodValueDto,
  type PutSupportLogEntryDto,
  type PutSupportLogEntryResponseDto,
  type SupportLogDayResponseDto,
  type SupportLogSummaryResponseDto,
  type UpdateCostLineDto,
  type WorkerListItemDto,
} from '@artic/api-client';
import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';
import { useRef } from 'react';

import { expectData } from '../_shared/api';
import type { CostGroup } from './types';
import type { CostLineTarget, WriteTarget } from './write-target';

export const OPERATING_COSTS_KEY = 'operating-costs' as const;

/**
 * Every Cost to Produce query hangs off the Operating Costs key. It lives here
 * (not in cost-to-produce/queries.ts, which re-exports it) because a baseline
 * write has to invalidate Cost to Produce, and the reverse import would be a
 * cycle.
 */
export const COST_TO_PRODUCE_KEY = 'cost-to-produce' as const;

// ── Conflict surface (409 → typed error for dialogs) ─────────────────────────

export class OperatingCostConflictError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'OperatingCostConflictError';
  }
}

/**
 * Converts operating-cost API conflicts into a typed error.
 *
 * @param err - The error to inspect
 * @throws `OperatingCostConflictError` for HTTP 409 API errors
 * @throws The original error for all other errors
 */
function mapConflict(err: unknown): never {
  if (err instanceof ApiError && err.status === 409) {
    const body = err.bodyAsRecord;
    const rawMessage = body?.['message'];
    throw new OperatingCostConflictError(typeof rawMessage === 'string' ? rawMessage : err.message);
  }
  throw err;
}

/**
 * The Cost Lines a scope has not enabled yet — every Cost Category (predefined
 * and the partner's own) and, for Value-adding, every operation without an
 * active line there. The rule lives server-side, shared by both scopes.
 *
 * Keyed under `'lines'` so the existing line invalidation refreshes it after
 * every create / archive / unarchive.
 *
 * @param target - The factory, or the partner for All factories
 * @param group - The Cost Group
 */
export function availableCostLinesQueryOptions(
  target: CostLineTarget | undefined,
  group: CostGroup,
) {
  const scopeId = target?.kind === 'factory' ? target.factoryId : target?.partnerId;
  return queryOptions({
    queryKey: [
      OPERATING_COSTS_KEY,
      'lines',
      'available',
      target?.kind ?? 'none',
      scopeId,
      group,
    ] as const,
    queryFn: async ({ signal }) => {
      if (!target) throw new Error('a factory or partner is required');
      const res =
        target.kind === 'factory'
          ? await operatingCostsControllerListAvailableCostLines(
              target.factoryId,
              { group },
              { signal },
            )
          : await allFactoriesBaselinesControllerListAvailableCostLines(
              target.partnerId,
              { group },
              { signal },
            );
      return expectData<AvailableCostLinesResponseDto>(res);
    },
    enabled: !!target,
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  });
}

/**
 * Configures a query for cost lines belonging to a factory, optionally filtered by group and archive visibility.
 *
 * @param factoryId - The factory identifier
 * @param group - The cost group used to filter the results
 * @param includeArchived - Whether archived cost lines are included
 */
export function costLinesQueryOptions(
  factoryId: string | undefined,
  group?: CostGroup,
  includeArchived = false,
) {
  return queryOptions({
    queryKey: [OPERATING_COSTS_KEY, 'lines', factoryId, group ?? 'all', includeArchived] as const,
    queryFn: async ({ signal }) => {
      if (!factoryId) throw new Error('factoryId is required');
      const res = await operatingCostsControllerListCostLines(
        factoryId,
        { ...(group ? { group } : {}), ...(includeArchived ? { include_archived: 'true' } : {}) },
        { signal },
      );
      return expectData<CostLinesResponseDto>(res);
    },
    enabled: !!factoryId,
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  });
}

/**
 * Configures a query for baseline values within a factory, group, and date range.
 *
 * @param factoryId - The factory identifier.
 * @param group - The cost group to query.
 * @param from - The start of the date range.
 * @param to - The end of the date range.
 * @param includeArchived - Whether to include archived cost lines.
 */
export function baselinesQueryOptions(
  factoryId: string | undefined,
  group: CostGroup,
  from: string,
  to: string,
  includeArchived = false,
) {
  return queryOptions({
    queryKey: [
      OPERATING_COSTS_KEY,
      'baselines',
      factoryId,
      group,
      from,
      to,
      includeArchived,
    ] as const,
    queryFn: async ({ signal }) => {
      if (!factoryId) throw new Error('factoryId is required');
      const res = await operatingCostsControllerGetBaselines(
        factoryId,
        { group, from, to, ...(includeArchived ? { include_archived: 'true' } : {}) },
        { signal },
      );
      return expectData<BaselinesResponseDto>(res);
    },
    enabled: !!factoryId,
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  });
}

/**
 * The All factories read of one group (ENG-921 D): every Cost Line fanned to
 * one row per factory, with each figure's provenance — keyed, derived from the
 * block by Shift Minutes, or summed from the factories.
 */
export function allFactoriesBaselinesQueryOptions(
  partnerId: string | undefined,
  group: CostGroup,
  from: string,
  to: string,
  includeArchived = false,
) {
  return queryOptions({
    queryKey: [
      OPERATING_COSTS_KEY,
      'baselines',
      'all',
      partnerId,
      group,
      from,
      to,
      includeArchived,
    ] as const,
    queryFn: async ({ signal }) => {
      if (!partnerId) throw new Error('partnerId is required');
      const res = await allFactoriesBaselinesControllerGetBaselines(
        partnerId,
        { group, from, to, ...(includeArchived ? { include_archived: 'true' } : {}) },
        { signal },
      );
      return expectData<AllFactoriesBaselinesResponseDto>(res);
    },
    enabled: !!partnerId,
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  });
}

/**
 * Configures a query for a worker's support-log entries on a specific date.
 *
 * @param factoryId - The factory identifier.
 * @param workerId - The worker identifier.
 * @param date - The date for which to retrieve support-log entries.
 */
export function supportLogDayQueryOptions(
  factoryId: string | undefined,
  workerId: string | undefined,
  date: string,
) {
  return queryOptions({
    queryKey: [OPERATING_COSTS_KEY, 'support-log', factoryId, workerId, date] as const,
    queryFn: async ({ signal }) => {
      if (!factoryId || !workerId) throw new Error('factoryId and workerId are required');
      const res = await supportLogControllerGetDay(
        factoryId,
        { worker_id: workerId, date },
        { signal },
      );
      return expectData<SupportLogDayResponseDto>(res);
    },
    enabled: !!factoryId && !!workerId,
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  });
}

/**
 * Configures a query for support-log summary data over a specified time window.
 *
 * @param factoryId - The factory identifier used to retrieve the summary
 * @param start - The start date for the summary
 * @param window - The summary period: day, week, or month
 * @param enabled - Whether the query is enabled when `factoryId` is available
 * @returns React Query options for fetching the support-log summary
 */
export function supportLogSummaryQueryOptions(
  factoryId: string | undefined,
  start: string,
  window: 'day' | 'week' | 'month',
  enabled: boolean,
) {
  return queryOptions({
    queryKey: [OPERATING_COSTS_KEY, 'support-log-summary', factoryId, start, window] as const,
    queryFn: async ({ signal }) => {
      if (!factoryId) throw new Error('factoryId is required');
      const res = await supportLogControllerGetSummary(factoryId, { start, window }, { signal });
      return expectData<SupportLogSummaryResponseDto>(res);
    },
    enabled: !!factoryId && enabled,
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  });
}

/**
 * Creates query options for retrieving workers associated with a partner.
 *
 * @param partnerId - The partner identifier used to scope the workers
 */
export function workersQueryOptions(partnerId: string | undefined) {
  return queryOptions({
    queryKey: ['workers', partnerId] as const,
    queryFn: async ({ signal }) => {
      if (!partnerId) throw new Error('partnerId is required');
      const res = await workersControllerListWorkers({ partner_id: partnerId }, { signal });
      return expectData<WorkerListItemDto[]>(res);
    },
    enabled: !!partnerId,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}

/**
 * Configures a query for projects associated with a partner.
 *
 * @param partnerId - The partner identifier used to load projects
 */
export function partnerProjectsQueryOptions(partnerId: string | undefined) {
  return queryOptions({
    queryKey: [OPERATING_COSTS_KEY, 'projects', partnerId] as const,
    queryFn: async ({ signal }) => {
      if (!partnerId) throw new Error('partnerId is required');
      const res = await projectsControllerListProjects({ partner_id: partnerId }, { signal });
      return expectData<ProjectListItemDto[]>(res);
    },
    enabled: !!partnerId,
    staleTime: 60_000,
    placeholderData: keepPreviousData,
  });
}

/**
 * Configures a query for audit history associated with a cost line.
 *
 * @param enabled - Whether the query should be enabled when required identifiers are available
 * @returns Query options for retrieving up to 200 audit records for the cost line
 */
/**
 * Audited changes under one parent row.
 *
 * ENG-921 generalised this from cost lines: a budget line's parent is its
 * PROJECT, so the parent id is whatever the caller audited against rather than
 * always a `cost_line_id`.
 */
export function auditHistoryQueryOptions(
  partnerId: string | undefined,
  parentId: string | undefined,
  enabled: boolean,
  options: { eventTargetTable?: string | undefined; lineKey?: string | undefined } = {},
) {
  return queryOptions({
    queryKey: [
      OPERATING_COSTS_KEY,
      'history',
      partnerId,
      parentId,
      options.eventTargetTable ?? null,
      options.lineKey ?? null,
    ] as const,
    queryFn: async ({ signal }) => {
      if (!partnerId || !parentId) throw new Error('partnerId and parentId are required');
      const res = await auditControllerList(
        {
          partner_id: partnerId,
          target_parent_id: parentId,
          // Narrow at the ENDPOINT, not after the fact: the 200-row limit is
          // applied server-side, so a parent with a busy audit trail would
          // otherwise page out the very rows the caller asked for and the list
          // would claim nothing had ever changed.
          ...(options.eventTargetTable ? { event_target_table: options.eventTargetTable } : {}),
          ...(options.lineKey !== undefined ? { line_key: options.lineKey } : {}),
          limit: 200,
        },
        { signal },
      );
      return expectData<AuditPageDto>(res);
    },
    enabled: !!partnerId && !!parentId && enabled,
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  });
}

/** @deprecated Prefer {@link auditHistoryQueryOptions}; kept for the cost-line callers. */
export const costLineHistoryQueryOptions = auditHistoryQueryOptions;

/**
 * Invalidation for the Operating Costs caches.
 *
 * Baselines and lines are invalidated partner-wide, not per factory: a figure
 * keyed at All factories changes every factory's derived share, and a figure
 * keyed at a factory changes the parent's sum or remainder — and only one
 * baseline query is ever mounted at a time, so the wider key costs nothing.
 * Cost to Produce reads the same baselines, so it follows every write.
 *
 * @param factoryId - Needed only for the factory-scoped Support Log caches
 */
function useInvalidate(factoryId?: string) {
  const queryClient = useQueryClient();
  return {
    lines: () => void queryClient.invalidateQueries({ queryKey: [OPERATING_COSTS_KEY, 'lines'] }),
    baselines: () =>
      void queryClient.invalidateQueries({ queryKey: [OPERATING_COSTS_KEY, 'baselines'] }),
    supportLog: () => {
      void queryClient.invalidateQueries({
        queryKey: [OPERATING_COSTS_KEY, 'support-log', factoryId],
      });
      void queryClient.invalidateQueries({
        queryKey: [OPERATING_COSTS_KEY, 'support-log-summary', factoryId],
      });
    },
    history: () =>
      void queryClient.invalidateQueries({ queryKey: [OPERATING_COSTS_KEY, 'history'] }),
    costToProduce: () =>
      void queryClient.invalidateQueries({
        queryKey: [OPERATING_COSTS_KEY, COST_TO_PRODUCE_KEY],
      }),
  };
}

/**
 * Creates a Cost Line in a factory, or an All factories line for the partner.
 *
 * @param target - Where the line lives
 */
export function useCreateCostLineFor(target: CostLineTarget) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (input: CreateCostLineDto) => {
      try {
        const res =
          target.kind === 'factory'
            ? await operatingCostsControllerCreateCostLine(target.factoryId, input)
            : await allFactoriesBaselinesControllerCreateLine(target.partnerId, input);
        return expectData<CostLineDto>(res);
      } catch (err) {
        mapConflict(err);
      }
    },
    onSuccess: () => {
      invalidate.lines();
      invalidate.baselines();
    },
  });
}

/** The type a custom-line attempt created, kept for a retry of the same input. */
type CreatedCustomType = { group: CostGroup; label: string; category: CostCategoryDto };

/**
 * "Add Custom Cost Line": creates a Cost Category of the partner's own, then
 * the line for it where the caller stands — ONE mutation, two requests. The
 * line's name and code default to the type's server-side.
 *
 * The two steps are not atomic: when the line request fails the type already
 * exists, and creating it again would 409. So the type created by an attempt
 * is remembered, and a retry with the same name skips straight to the line.
 * Either way both caches refresh, so the new type shows in the
 * not-yet-enabled table.
 *
 * @param partnerId - The partner the type belongs to
 * @param target - Where the first line goes
 */
export function useCreateCustomCostLine(partnerId: string | undefined, target: CostLineTarget) {
  const invalidate = useInvalidate();
  const created = useRef<CreatedCustomType | null>(null);
  return useMutation({
    mutationFn: async (input: CreateCustomCostCategoryDto) => {
      const partner = requirePartner(partnerId);
      try {
        const previous = created.current;
        const category =
          previous && previous.group === input.cost_group && previous.label === input.label
            ? previous.category
            : expectData<CostCategoryDto>(
                await partnerCostCategoriesControllerCreate(partner, input),
              );
        created.current = { group: input.cost_group, label: input.label, category };
        const line: CreateCostLineDto = {
          cost_group: input.cost_group,
          cost_category: category.key,
        };
        const res =
          target.kind === 'factory'
            ? await operatingCostsControllerCreateCostLine(target.factoryId, line)
            : await allFactoriesBaselinesControllerCreateLine(target.partnerId, line);
        // Done: the next identical request is a genuine duplicate and should 409.
        created.current = null;
        return { category, line: expectData<CostLineDto>(res) };
      } catch (err) {
        mapConflict(err);
      }
    },
    onSettled: () => {
      invalidate.lines();
      invalidate.baselines();
    },
  });
}

/**
 * Archives a custom Cost Category: it leaves every add list, while lines
 * already created from it keep their figures and label.
 *
 * @param partnerId - The partner the type belongs to
 */
export function useArchiveCustomCostCategory(partnerId: string | undefined) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async ({ group, key }: { group: CostGroup; key: string }) => {
      const res = await partnerCostCategoriesControllerArchive(
        requirePartner(partnerId),
        group,
        key,
      );
      return expectData<CostCategoryDto>(res);
    },
    onSuccess: () => {
      invalidate.lines();
    },
  });
}

/**
 * Renames a Cost Line (factory or All factories) or sets its Cost Code.
 *
 * @param target - Where the line lives
 */
export function useUpdateCostLineFor(target: CostLineTarget) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async ({ costLineId, input }: { costLineId: string; input: UpdateCostLineDto }) => {
      try {
        const res =
          target.kind === 'factory'
            ? await operatingCostsControllerUpdateCostLine(target.factoryId, costLineId, input)
            : await allFactoriesBaselinesControllerUpdateLine(target.partnerId, costLineId, input);
        return expectData<CostLineDto>(res);
      } catch (err) {
        mapConflict(err);
      }
    },
    onSuccess: () => {
      invalidate.lines();
      invalidate.baselines();
      invalidate.history();
    },
  });
}

/**
 * Archives or unarchives a Cost Line (factory or All factories).
 *
 * @param target - Where the line lives
 */
export function useSetCostLineArchivedFor(target: CostLineTarget) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async ({ costLineId, archived }: { costLineId: string; archived: boolean }) => {
      const res =
        target.kind === 'factory'
          ? archived
            ? await operatingCostsControllerArchiveCostLine(target.factoryId, costLineId)
            : await operatingCostsControllerUnarchiveCostLine(target.factoryId, costLineId)
          : archived
            ? await allFactoriesBaselinesControllerArchiveLine(target.partnerId, costLineId)
            : await allFactoriesBaselinesControllerUnarchiveLine(target.partnerId, costLineId);
      return expectData<CostLineDto>(res);
    },
    onSuccess: () => {
      invalidate.lines();
      invalidate.baselines();
      invalidate.history();
      invalidate.costToProduce();
    },
  });
}

function requirePartner(partnerId: string | undefined): string {
  if (!partnerId) throw new Error('partnerId is required');
  return partnerId;
}

const PERIOD_CONFLICT_MESSAGE =
  'The factories key this group at different baseline periods, so nothing can be keyed at All factories';

/**
 * Keys a baseline wherever the row says it lives: a factory's own line, a
 * factory's row under an All factories line (created on first key), or the
 * All factories block itself.
 *
 * @param partnerId - The partner, for the All factories routes
 */
export function useSetBaselineAt(partnerId: string | undefined) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async ({ target, input }: { target: WriteTarget; input: PutBaselineDto }) => {
      switch (target.kind) {
        case 'factory':
          return expectData<BaselineVersionDto>(
            await operatingCostsControllerPutBaseline(target.factoryId, target.costLineId, input),
          );
        case 'child':
          return expectData<BaselineVersionDto>(
            await allFactoriesBaselinesControllerPutChildBaseline(
              requirePartner(partnerId),
              target.costGroup,
              target.lineKey,
              target.factoryId,
              input,
            ),
          );
        case 'parent':
          return expectData<BaselineVersionDto>(
            await allFactoriesBaselinesControllerPutParentBaseline(
              requirePartner(partnerId),
              target.costGroup,
              target.lineKey,
              input,
            ),
          );
        case 'none':
          throw new Error(PERIOD_CONFLICT_MESSAGE);
      }
    },
    onSuccess: (_result, { target }) => {
      invalidate.baselines();
      invalidate.history();
      invalidate.costToProduce();
      if (createsLines(target)) invalidate.lines();
    },
  });
}

/**
 * A child write may create the factory's line, and a parent write the All
 * factories line — each changes the lines the Support Log and the
 * not-yet-enabled table list (ENG-950). A factory line already exists.
 */
function createsLines(target: WriteTarget): boolean {
  return target.kind === 'child' || target.kind === 'parent';
}

/**
 * Overrides one period's figure (or sets its note) wherever the row lives —
 * see {@link useSetBaselineAt} for the targets.
 *
 * @param partnerId - The partner, for the All factories routes
 */
export function useSetPeriodValueAt(partnerId: string | undefined) {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async ({
      target,
      periodStart,
      input,
    }: {
      target: WriteTarget;
      periodStart: string;
      input: PutPeriodValueDto;
    }) => {
      switch (target.kind) {
        case 'factory':
          return expectData<PeriodValueResponseDto>(
            await operatingCostsControllerPutPeriodValue(
              target.factoryId,
              target.costLineId,
              periodStart,
              input,
            ),
          );
        case 'child':
          return expectData<PeriodValueResponseDto>(
            await allFactoriesBaselinesControllerPutChildPeriodValue(
              requirePartner(partnerId),
              target.costGroup,
              target.lineKey,
              target.factoryId,
              periodStart,
              input,
            ),
          );
        case 'parent':
          return expectData<PeriodValueResponseDto>(
            await allFactoriesBaselinesControllerPutParentPeriodValue(
              requirePartner(partnerId),
              target.costGroup,
              target.lineKey,
              periodStart,
              input,
            ),
          );
        case 'none':
          throw new Error(PERIOD_CONFLICT_MESSAGE);
      }
    },
    onSuccess: (_result, { target }) => {
      invalidate.baselines();
      invalidate.history();
      invalidate.costToProduce();
      if (createsLines(target)) invalidate.lines();
    },
  });
}

/**
 * Provides a mutation for saving a support-log entry and refreshing related cached data.
 *
 * @param factoryId - The factory associated with the support-log entry
 * @returns The support-log entry mutation controls and state
 */
export function useSaveSupportLogEntry(factoryId: string) {
  const invalidate = useInvalidate(factoryId);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: PutSupportLogEntryDto) => {
      const res = await supportLogControllerPutEntry(factoryId, input);
      return expectData<PutSupportLogEntryResponseDto>(res);
    },
    onSuccess: async (result, input) => {
      queryClient.setQueryData<SupportLogDayResponseDto>(
        supportLogDayQueryOptions(factoryId, input.worker_id, input.entry_date).queryKey,
        (current) => {
          if (!current) return current;
          const entries = result.entry
            ? current.entries.some((entry) => entry.cost_line_id === result.entry?.cost_line_id)
              ? current.entries.map((entry) =>
                  entry.cost_line_id === result.entry?.cost_line_id ? result.entry : entry,
                )
              : [...current.entries, result.entry]
            : current.entries.filter((entry) => entry.cost_line_id !== input.cost_line_id);
          return { ...current, entries, day_context: result.day_context };
        },
      );
      invalidate.supportLog();
      invalidate.history();
      if (input.line_key !== undefined) {
        // The write created the factory's line for a virtual row (ENG-950).
        // Hold the save open until the Log has re-read its lines, so the row
        // it keyed is the real line before anything else writes to it.
        invalidate.baselines();
        await queryClient.invalidateQueries({ queryKey: [OPERATING_COSTS_KEY, 'lines'] });
      }
    },
  });
}
